import { type ReactNode, useState } from 'react';
import { Splitter } from '@mantine/core';
import { readLocalStorageValue, type SplitterPaneSize } from '@mantine/hooks';

import classes from './resizable-split.module.css';

type SpacingToken = `--spacing-${string}`;

const MAIN_PANE_SIZE = 100;

function pxSize(value: number) {
    return `${String(value)}px` as `${number}px`;
}

export function ResizableSplit({
    storageKey,
    sideDefault,
    sideMin,
    mainMin,
    main,
    side,
    orientation = 'horizontal',
    className,
    mainClassName,
    sideClassName,
}: Readonly<{
    storageKey: string;
    sideDefault: SpacingToken;
    sideMin: SpacingToken;
    mainMin: `${number}%`;
    main: ReactNode;
    side: ReactNode;
    orientation?: 'horizontal' | 'vertical';
    className?: string;
    mainClassName?: string;
    sideClassName?: string;
}>) {
    const [limits] = useState(() => {
        const rootStyle = getComputedStyle(document.documentElement);
        return {
            defaultPx: Number.parseFloat(rootStyle.getPropertyValue(sideDefault)),
            minPx: Number.parseFloat(rootStyle.getPropertyValue(sideMin)),
        };
    });
    const [sidePx, setSidePx] = useState(() => readLocalStorageValue({ key: storageKey, defaultValue: limits.defaultPx }));
    const hasSide = Boolean(side);
    const sizes: SplitterPaneSize[] = hasSide ? [MAIN_PANE_SIZE, pxSize(sidePx)] : [MAIN_PANE_SIZE];

    return (
        <Splitter
            className={className}
            classNames={{ handle: classes.handle, thumb: classes.thumb }}
            lineSize={1}
            orientation={orientation}
            sizes={sizes}
            onSizeChange={(next) => {
                const px = Number.parseFloat(String(next[1]));
                if (!Number.isFinite(px)) {
                    return;
                }
                setSidePx(px);
                localStorage.setItem(storageKey, JSON.stringify(px));
            }}
        >
            <Splitter.Pane className={mainClassName} defaultSize={MAIN_PANE_SIZE} min={mainMin}>
                {main}
            </Splitter.Pane>
            {hasSide && (
                <Splitter.Pane className={sideClassName} defaultSize={pxSize(limits.defaultPx)} min={pxSize(limits.minPx)}>
                    {side}
                </Splitter.Pane>
            )}
        </Splitter>
    );
}
